import { type NodeViewProps, NodeViewWrapper } from '@tiptap/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { formatBytes } from '@/entities/attachment';
import { documentMediaUrl } from '@/entities/document';
import { Icons } from '@/shared/ui';

import styles from './DocumentEditor.module.css';

const WIDTHS: readonly (number | null)[] = [240, 480, null];
/** The displayed width, in pixels. The height follows (the aspect ratio is never broken). */
const MIN_IMAGE_WIDTH = 40;
const MAX_IMAGE_WIDTH = 4000;
const ALIGNS = ['left', 'center', 'right'] as const;

/** Where the note's files are served: its own address, or (editing a template) the template's. */
function mediaUrlOf(props: NodeViewProps, mediaId: string): string {
  const options = props.extension.options as { documentId: string; mediaBase?: string };
  return options.mediaBase
    ? `${options.mediaBase}/${encodeURIComponent(mediaId)}`
    : documentMediaUrl(options.documentId, mediaId);
}

export function DocumentImageView(props: NodeViewProps) {
  const { t } = useTranslation();
  const { node, updateAttributes, deleteNode, selected, editor } = props;
  const { mediaId, alt, width, align } = node.attrs as {
    mediaId: string | null;
    alt: string | null;
    width: number | null;
    align: 'left' | 'center' | 'right' | null;
  };
  const editable = editor.isEditable;
  const [editingAlt, setEditingAlt] = useState(false);
  return (
    <NodeViewWrapper
      className={styles.image}
      data-align={align ?? 'left'}
      data-selected={selected || undefined}
      data-testid="document-image"
    >
      {mediaId ? (
        <img
          src={`${mediaUrlOf(props, mediaId)}/preview?variant=large`}
          alt={alt ?? ''}
          style={width ? { width: `${String(width)}px` } : undefined}
          loading="lazy"
          draggable={false}
        />
      ) : (
        <span>{alt}</span>
      )}
      {editable && selected && (
        <span className={styles.imageBar} contentEditable={false}>
          {WIDTHS.map((value) => (
            <button
              key={value ?? 'full'}
              type="button"
              aria-pressed={width === value}
              onClick={() => {
                updateAttributes({ width: value });
              }}
            >
              {value === null ? t('documents.editor.imageFull') : `${String(value)}px`}
            </button>
          ))}
          <input
            key={width ?? 'auto'}
            className={styles.widthInput}
            type="number"
            inputMode="numeric"
            min={MIN_IMAGE_WIDTH}
            max={MAX_IMAGE_WIDTH}
            step={10}
            defaultValue={width ?? ''}
            placeholder="auto"
            aria-label={t('documents.editor.imageWidth')}
            data-testid="image-width-input"
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                event.currentTarget.blur();
              }
            }}
            onBlur={(event) => {
              const value = event.currentTarget.valueAsNumber;
              if (Number.isFinite(value)) {
                updateAttributes({
                  width: Math.min(MAX_IMAGE_WIDTH, Math.max(MIN_IMAGE_WIDTH, Math.round(value))),
                });
              } else {
                event.currentTarget.value = width === null ? '' : String(width);
              }
            }}
          />
          <button
            type="button"
            data-testid="image-reset"
            disabled={width === null && align === null}
            onClick={() => {
              updateAttributes({ width: null, align: null });
            }}
          >
            {t('documents.editor.imageReset')}
          </button>
          {ALIGNS.map((value) => (
            <button
              key={value}
              type="button"
              aria-pressed={(align ?? 'left') === value}
              onClick={() => {
                updateAttributes({ align: value });
              }}
            >
              {t(`documents.editor.align.${value}`)}
            </button>
          ))}
          {editingAlt ? (
            <input
              className={styles.altInput}
              aria-label={t('documents.editor.imageAlt')}
              defaultValue={alt ?? ''}
              maxLength={300}
              autoFocus
              data-testid="image-alt-input"
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  updateAttributes({ alt: event.currentTarget.value });
                  setEditingAlt(false);
                } else if (event.key === 'Escape') {
                  event.stopPropagation();
                  setEditingAlt(false);
                }
              }}
              onBlur={(event) => {
                updateAttributes({ alt: event.currentTarget.value });
                setEditingAlt(false);
              }}
            />
          ) : (
            <button
              type="button"
              data-testid="image-alt-edit"
              onClick={() => {
                setEditingAlt(true);
              }}
            >
              {t('documents.editor.imageAltShort')}
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              deleteNode();
            }}
          >
            {t('documents.editor.remove')}
          </button>
        </span>
      )}
    </NodeViewWrapper>
  );
}

export function DocumentFileView(props: NodeViewProps) {
  const { t, i18n } = useTranslation();
  const { node, deleteNode, selected, editor } = props;
  const { mediaId, filename, size, mime } = node.attrs as {
    mediaId: string | null;
    filename: string | null;
    size: number | null;
    mime: string | null;
  };
  const kind = (filename ?? '').split('.').pop()?.toUpperCase();
  return (
    <NodeViewWrapper
      className={styles.file}
      data-selected={selected || undefined}
      data-testid="document-file"
    >
      <span className={styles.fileIcon} aria-hidden>
        <Icons.file />
      </span>
      {mediaId && (
        <a
          href={`${mediaUrlOf(props, mediaId)}/download`}
          className={styles.fileName}
          title={filename ?? ''}
          download={filename ?? true}
          contentEditable={false}
        >
          {filename}
        </a>
      )}
      <span className={styles.fileMeta} contentEditable={false} title={mime ?? ''}>
        {kind ? `${kind} · ` : ''}
        {size !== null ? formatBytes(size, i18n.language) : ''}
      </span>
      {editor.isEditable && selected && (
        <button
          type="button"
          className={styles.fileRemove}
          onClick={() => {
            deleteNode();
          }}
        >
          {t('documents.editor.remove')}
        </button>
      )}
    </NodeViewWrapper>
  );
}
